<script setup>
import { onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  // Layer above the immersive catalog overlay (z 3000) when opened from there
  elevated: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'pay-credit', 'send-order'])

function close() {
  emit('update:modelValue', false)
}

function handleCredit() {
  emit('pay-credit')
}

function handleSendOrder() {
  emit('send-order')
}

function handleKeydown(e) {
  if (e.key === 'Escape' && props.modelValue) close()
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
  }
)

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = ''
})
</script>

<template>
  <Teleport to="body">
    <Transition name="finalize-modal">
      <div v-if="modelValue" class="finalize-overlay" :class="{ elevated }" @click="close">
        <div class="finalize-modal" role="dialog" aria-modal="true" @click.stop>
          <header class="finalize-header">
            <h2 class="finalize-title">סיום הזמנה</h2>
            <button class="finalize-close" aria-label="סגור" @click="close">
              <svg
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </header>

          <div class="finalize-body">
            <p class="finalize-subtitle">בחרו כיצד להמשיך:</p>

            <button class="finalize-btn finalize-btn-credit" @click="handleCredit">
              <span class="finalize-btn-icon">
                <svg
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <line x1="2" y1="10" x2="22" y2="10" />
                </svg>
              </span>
              <span class="finalize-btn-text">מעבר לתשלום באשראי</span>
            </button>

            <button class="finalize-btn finalize-btn-send" @click="handleSendOrder">
              <span class="finalize-btn-icon">
                <svg
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M22 2L11 13" />
                  <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                </svg>
              </span>
              <span class="finalize-btn-text">שליחת הזמנה</span>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.finalize-overlay {
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-lg);
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
}

.finalize-overlay.elevated {
  z-index: 3070;
}

.finalize-modal {
  width: 100%;
  max-width: 440px;
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.finalize-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--gradient-header);
  color: white;
}

.finalize-title {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
}

.finalize-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.finalize-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.finalize-body {
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.finalize-subtitle {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  text-align: center;
}

.finalize-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  color: white;
}

.finalize-btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.finalize-btn-credit {
  background: var(--color-primary);
  box-shadow: 0 2px 8px rgba(30, 41, 59, 0.3);
}

.finalize-btn-credit:hover {
  background: var(--color-primary-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(30, 41, 59, 0.35);
}

.finalize-btn-send {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
}

.finalize-btn-send:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
}

/* Animation */
.finalize-modal-enter-active,
.finalize-modal-leave-active {
  transition: opacity 0.2s ease;
}

.finalize-modal-enter-from,
.finalize-modal-leave-to {
  opacity: 0;
}

.finalize-modal-enter-active .finalize-modal,
.finalize-modal-leave-active .finalize-modal {
  transition: transform 0.2s ease;
}

.finalize-modal-enter-from .finalize-modal,
.finalize-modal-leave-to .finalize-modal {
  transform: scale(0.95);
}

/* Mobile fullscreen */
@media (max-width: 767px) {
  .finalize-overlay {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
  }

  .finalize-modal {
    max-width: 100%;
    height: 100%;
    border-radius: 0;
  }

  .finalize-body {
    flex: 1;
    justify-content: center;
    padding: var(--spacing-xl) var(--spacing-lg);
  }

  .finalize-btn {
    padding: var(--spacing-lg);
    font-size: var(--font-size-lg);
  }
}
</style>
